import { useHoldRepeat } from '@src/hooks/useHoldRepeat';
import ScaleIcon from '@src/svgs/ScaleIcon';
import { useRef } from 'react';
import styled from 'styled-components';

import { ToolbarStepButton } from './FloatingToolbarSurface';

import type { KeyboardEvent, ReactNode, TouchEvent } from 'react';

interface ToolbarStepperProps {
  value: number;
  min: number;
  max: number;
  step: number;
  /** Called with the next, already-clamped value. */
  onChange: (next: number) => void;
  decreaseLabel: string;
  increaseLabel: string;
  /** What sits between − and + (the zoom icon, the font size). */
  children: ReactNode;
}

/**
 * The one −/+ stepper for element toolbars (PLAT-12099): zoom on the image and
 * video bars, font size on the text bar. Click for one step, hold to repeat;
 * mouse, touch and arrow / Enter / Space keys all do the same.
 *
 * Each step reads the latest value from a ref — the value it last sent, or the
 * prop once the bar re-renders — not the value captured when the hold started,
 * so a hold keeps advancing instead of freezing (the PLAT-12088 font-size bug).
 * At a bound the button disables and gets no mouseup, so the hold ends here
 * rather than re-sending the bound every tick.
 */
export const ToolbarStepper = ({
  value,
  min,
  max,
  step,
  onChange,
  decreaseLabel,
  increaseLabel,
  children,
}: ToolbarStepperProps) => {
  const valueRef = useRef(value);
  valueRef.current = value;

  const holdDec = useHoldRepeat();
  const holdInc = useHoldRepeat();

  const apply = (next: number) => {
    const clamped = Math.min(max, Math.max(min, next));
    if (clamped !== valueRef.current) {
      onChange(clamped);
      valueRef.current = clamped;
    }
    if (clamped === min || clamped === max) {
      holdDec.stop();
      holdInc.stop();
    }
  };
  const stepDown = () => apply(valueRef.current - step);
  const stepUp = () => apply(valueRef.current + step);
  const canDec = value > min;
  const canInc = value < max;

  const buttonHandlers = (
    enabled: boolean,
    hold: ReturnType<typeof useHoldRepeat>,
    action: () => void,
    arrowKey: 'ArrowDown' | 'ArrowUp',
  ) => ({
    onMouseDown: () => enabled && hold.start(action),
    onMouseUp: hold.stop,
    onMouseLeave: hold.stop,
    onTouchStart: (e: TouchEvent) => {
      if (!enabled) return;
      // Without this the synthesized mousedown fires the action a second time.
      e.preventDefault();
      hold.start(action);
    },
    onTouchEnd: hold.stop,
    onTouchCancel: hold.stop,
    onKeyDown: (e: KeyboardEvent) => {
      if (
        enabled &&
        (e.key === 'Enter' || e.key === ' ' || e.key === arrowKey)
      ) {
        e.preventDefault();
        action();
      }
    },
  });

  return (
    <SStepper>
      <ToolbarStepButton
        disabled={!canDec}
        aria-label={decreaseLabel}
        {...buttonHandlers(canDec, holdDec, stepDown, 'ArrowDown')}
      >
        −
      </ToolbarStepButton>
      <SMiddle>{children}</SMiddle>
      <ToolbarStepButton
        disabled={!canInc}
        aria-label={increaseLabel}
        {...buttonHandlers(canInc, holdInc, stepUp, 'ArrowUp')}
      >
        +
      </ToolbarStepButton>
    </SStepper>
  );
};

/** The zoom stepper of the image and video bars. */
export const ToolbarZoomStepper = (
  props: Omit<
    ToolbarStepperProps,
    'decreaseLabel' | 'increaseLabel' | 'children'
  >,
) => (
  <ToolbarStepper {...props} decreaseLabel="Zoom out" increaseLabel="Zoom in">
    <ScaleIcon width="14" height="14" strokeColor="currentColor" />
  </ToolbarStepper>
);

const SStepper = styled.div`
  display: flex;
  align-items: center;
  gap: 0;
`;

const SMiddle = styled.span`
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 24px;
  height: 28px;
  padding: 0 2px;
  color: var(--text-secondary);
  font-size: var(--text-sm);
  font-weight: var(--font-weight-medium);
`;
